import { DashboardFEFilterTagList, DashboardFEFilterTag } from '../types/dashboard.types';
import _ from 'lodash';
import { WhereCondition, MarketingLevel } from '../types/server.types';
import { getMergedDashboardFilter } from '../dashboardFilter.js';
import { both, complement, isNil, isEmpty, filter, reduce, map, compose, when, is, defaultTo, objOf } from 'ramda';

export function convertDashboardFilterTagListToParsedList(dashboardFilterTagList: DashboardFEFilterTagList) {
  // This part used to run on the FE in the function 'parseTagsToParam'
  const parsedFilters: { [key: string]: any[] } = {};
  for (let filter of dashboardFilterTagList) {
    let { type, filterBy } = filter;
    // const isBreakdownLabel = type === 'breakdownLabels';
    let value: string | number = null;
    if (filter.type == 'breakdownLabels') {
      value = filter.filterBy.labelId;
    } else if (typeof filterBy === 'object' && 'value' in filterBy) {
      value = filterBy.value;
    } else if (typeof filterBy == 'string') {
      value = filterBy;
    }
    if (value) {
      if (!parsedFilters[type]) parsedFilters[type] = [];
      if (filter.type == 'breakdownLabels') {
        parsedFilters[type].push({ dimensionId: filter.filterBy.dimensionId, labelId: filter.filterBy.labelId });
      } else {
        parsedFilters[type].push(filterBy);
      }
    }
  }
  return parsedFilters;
}

export function convertTableDashboardFilterTagListToDataSourceQueryWhereConditions(
  dashboardFilterTagList: DashboardFEFilterTagList,
): WhereCondition[] {
  const parsedFilters = convertDashboardFilterTagListToParsedList(dashboardFilterTagList);
  // This part used to run in the common project in the function 'getTableDashboardFilterToDataSourceQueryWhereConditions'
  return Object.entries(parsedFilters)
    .flatMap(([field, filter]): WhereCondition[] | WhereCondition => {
      filter = _.uniq(filter);
      // if (Array.isArray(filter) && filter.length == 0) {
      //   return [];
      // }
      if (Array.isArray(filter) && filter.some(f => f?.level)) {
        const levelFilters = _.groupBy(filter, 'level');
        return Object.values(levelFilters).map(levelFilter => {
          const values = _.uniq(levelFilter.map(f => f.value));
          const operator = ['title', 'titles'].includes(field) ? 'LIKE' : 'IN';
          if (field === 'ids') field = 'channelId';
          const cond: WhereCondition = { field: field, level: levelFilter[0].level, operator, values };
          return cond;
        });
      } else if (field == 'metrics') {
        return filter.map(metricFilter => {
          let operator = null;
          if (metricFilter.operator == 'LESS_THAN') {
            operator = 'LESS_THAN';
          } else if (metricFilter.operator == 'GREATER_THAN') {
            operator = 'GREATER_THAN';
          } else if (metricFilter.operator == 'EQUAL') {
            operator = 'EQUAL';
          } else {
            throw new Error(`Unknown  metric filter operator '${metricFilter.operator}'`);
          }
          return {
            field: metricFilter.field,
            table: 'report',
            operator,
            value: metricFilter.value,
          };
        });
      } else if (['adAccountIds', 'campaignIds', 'adSetIds', 'adIds'].includes(field)) {
        const cond: WhereCondition = {
          field: 'channelId',
          level: field.replace('Ids', '') as MarketingLevel,
          operator: 'IN',
          values: filter,
        };
        return cond;
      } else {
        const cond: WhereCondition = {
          field: field,
          operator: 'IN',
          values: Array.isArray(filter) ? filter : [filter],
        };
        return cond;
      }
    })
    .flat();
}

const isValueExist = both(complement(isNil), complement(isEmpty));

const clearEmptyFilter = filter(isValueExist);

export const mergeFiltersToParams = compose(
  reduce(getMergedDashboardFilter, {}),
  map(
    compose(
      clearEmptyFilter,
      when(
        is(Array),
        // @ts-ignore
        compose(convertDashboardFilterTagListToParsedList, objOf('filters')),
      ),
      defaultTo({}),
    ),
  ),
  defaultTo([]),
);

export const mergeFiltersToParamsWithoutClear = compose(
  reduce(getMergedDashboardFilter, {}),
  map(
    compose(
      when(
        is(Array),
        compose(
          convertDashboardFilterTagListToParsedList,
          // @ts-ignore
          objOf('filters'),
        ),
      ),
      defaultTo({}),
    ),
  ),
  defaultTo<Record<string, any>[]>([]),
);
